import { useEffect, useState } from 'react';
import { AlertBannerType, ExAlertBanner, ExLoader } from '@boomi/exosphere';
import { getConnector, type Connector } from '../connectors/connectors';
import { getTools, type Tool } from './tools';
import SearchInput from '../shared/SearchInput';
import styles from '../shared/Form.module.css';
import toolStyles from './ConnectorTools.module.css';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';

interface ConnectorToolsProps {
    connectorId: string;
}

const ConnectorTools = ({ connectorId }: ConnectorToolsProps) => {
    const [connector, setConnector] = useState<Connector | null>(null);
    const [tools, setTools] = useState<Tool[]>([]);
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([getConnector(connectorId), getTools(connectorId)])
            .then(([connectorData, toolsData]) => {
                setConnector(connectorData);
                setTools(
                    [...toolsData].sort((a, b) =>
                        a.annotations.title.localeCompare(b.annotations.title),
                    ),
                );
            })
            .catch(() => setError(GENERIC_ERROR_MESSAGE))
            .finally(() => setLoading(false));
    }, [connectorId]);

    const filteredTools = search
        ? tools.filter(
              (t) =>
                  t.annotations.title.toLowerCase().includes(search.toLowerCase()) ||
                  t.name.toLowerCase().includes(search.toLowerCase()) ||
                  t.description.toLowerCase().includes(search.toLowerCase()),
          )
        : tools;

    if (error) {
        return (
            <ExAlertBanner type={AlertBannerType.ERROR} open>
                {error}
            </ExAlertBanner>
        );
    }

    return (
        <>
            <h3 className={styles.sectionHeading}>Manage Tools ({tools.length})</h3>
            <SearchInput value={search} placeholder="Search tools" onSearch={setSearch} />
            <table className={toolStyles.table}>
                <thead className={toolStyles.tableHead}>
                    <tr>
                        <th>Tool Name</th>
                        <th>Description</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={2}>
                                <ExLoader label="Loading tools…" />
                            </td>
                        </tr>
                    ) : (
                        filteredTools.map((tool) => (
                            <tr key={tool.name} className={toolStyles.row}>
                                <td>
                                    <div className={toolStyles.toolNameCell}>
                                        {connector?.icon && (
                                            <img
                                                src={connector.icon}
                                                alt={connector.name}
                                                className={toolStyles.connectorIcon}
                                            />
                                        )}
                                        <div className={toolStyles.toolNameInfo}>
                                            <span className={toolStyles.toolTitle}>
                                                {tool.annotations.title}
                                            </span>
                                            <span className={toolStyles.toolId}>{tool.name}</span>
                                        </div>
                                    </div>
                                </td>
                                <td className={toolStyles.toolDescription} title={tool.description}>
                                    {tool.description}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </>
    );
};

export default ConnectorTools;
